import type { CommentaryIntensity } from '@fbwl/shared';

interface SpeechRequest {
  readonly text: string;
  readonly intensity: CommentaryIntensity;
}

/**
 * Text-to-speech commentary.
 *
 * Uses the browser's own speech synthesis, which is the only option that works
 * inside an OBS browser source without a paid cloud voice and a network round
 * trip that would put the call three seconds behind the picture.
 *
 * The queue is short and lossy on purpose. During a busy final the engine can
 * produce lines faster than anyone can say them; a backlog would leave the desk
 * describing a match that finished a minute ago, so old low-priority lines are
 * dropped rather than spoken late.
 */
export class SpeechEngine {
  private readonly queue: SpeechRequest[] = [];
  private voice: SpeechSynthesisVoice | null = null;
  private speaking = false;
  private enabled: boolean;
  private volume: number;

  /** Notified when a line starts and ends, so the mix can duck the music. */
  onSpeakingChange: ((speaking: boolean, seconds: number) => void) | null = null;

  constructor(enabled = true, volume = 1) {
    this.enabled = enabled && typeof window !== 'undefined' && 'speechSynthesis' in window;
    this.volume = volume;

    if (this.enabled) {
      this.pickVoice();
      // Voice lists populate asynchronously in every browser worth supporting.
      window.speechSynthesis.addEventListener('voiceschanged', () => this.pickVoice());
    }
  }

  get isAvailable(): boolean {
    return this.enabled;
  }

  get voiceName(): string {
    return this.voice?.name ?? 'default';
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.cancel();
  }

  say(text: string, intensity: CommentaryIntensity): void {
    if (!this.enabled || text.length === 0) return;

    // An epic line is worth interrupting for - a champion has just been crowned
    // and nobody wants to hear about a group-stage strike over the fireworks.
    if (intensity === 'epic') {
      this.queue.length = 0;
      this.cancel();
      this.queue.push({ text, intensity });
      this.pump();
      return;
    }

    if (this.queue.length >= 2) this.queue.shift();
    this.queue.push({ text, intensity });
    this.pump();
  }

  cancel(): void {
    if (!this.enabled) return;
    window.speechSynthesis.cancel();
    this.speaking = false;
  }

  private pump(): void {
    if (!this.enabled || this.speaking) return;

    const next = this.queue.shift();
    if (!next) return;

    const utterance = new SpeechSynthesisUtterance(next.text);
    if (this.voice) utterance.voice = this.voice;
    utterance.volume = this.volume;

    // Faster and higher as the moment gets bigger - the cheapest way to make a
    // synthetic voice sound like it cares.
    utterance.rate = next.intensity === 'epic' ? 1.14 : next.intensity === 'hype' ? 1.06 : 0.98;
    utterance.pitch = next.intensity === 'epic' ? 1.12 : next.intensity === 'hype' ? 1.05 : 1;

    const estimatedSeconds = Math.min(9, Math.max(1.4, next.text.length / 13));

    utterance.onstart = () => {
      this.speaking = true;
      this.onSpeakingChange?.(true, estimatedSeconds);
    };
    const finish = (): void => {
      this.speaking = false;
      this.onSpeakingChange?.(false, 0);
      this.pump();
    };
    utterance.onend = finish;
    utterance.onerror = finish;

    // Chrome pauses synthesis after ~15s of a backgrounded tab; a resume before
    // every utterance is the standard workaround and is harmless otherwise.
    window.speechSynthesis.resume();
    window.speechSynthesis.speak(utterance);
  }

  private pickVoice(): void {
    if (!this.enabled) return;
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) return;

    // Prefer a natural-sounding English voice; the commentary is written in
    // English and a mismatched locale mangles nation names badly.
    const preferred = [
      /Google UK English Male/i,
      /Microsoft (Ryan|Guy|Christopher).*Online/i,
      /Google US English/i,
      /Daniel/i,
      /en[-_]GB/i,
      /en[-_]US/i,
    ];

    for (const pattern of preferred) {
      const match = voices.find((v) => pattern.test(v.name) || pattern.test(v.lang));
      if (match) {
        this.voice = match;
        return;
      }
    }

    this.voice = voices.find((v) => v.lang.startsWith('en')) ?? voices[0] ?? null;
  }
}
